import React from "react"
import { Button } from "@ciq/shared/components/buttons"
import { ReactComponent as CheckIcon } from "@ciq/shared/assets/icons/item_selected_icon.svg"

/**
 * Format a timestamp for display in the success banner.
 * Converts UTC timestamp to user's local timezone.
 * @param {number|string} value - Epoch timestamp (seconds) or ISO 8601 timestamp string
 * @returns {string} Formatted date string in user's local timezone
 */
export function formatTimestamp(value) {
  let date

  // Handle epoch timestamps (integers from API)
  // API returns epoch in seconds, JavaScript Date expects milliseconds
  if (typeof value === "number") {
    date = new Date(value * 1000)
  } else {
    // Handle ISO string timestamps
    // Ensure the timestamp is treated as UTC if no timezone is specified
    let normalizedTimestamp = value
    if (!value.endsWith("Z") && !value.includes("+") && !value.includes("-", 10)) {
      normalizedTimestamp = value + "Z"
    }
    date = new Date(normalizedTimestamp)
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  })
}

/**
 * @typedef {Object} SuccessBannerProps
 * @property {number|string} timestamp - Epoch timestamp (seconds) or ISO 8601 string when the upload completed
 * @property {Function} onUploadAnother - Callback when user clicks "Upload Another"
 */

/**
 * Success banner displayed after zone data upload completes.
 * Shows confirmation message with timestamp and action buttons.
 *
 * @param {SuccessBannerProps} props
 */
export function SuccessBanner({ timestamp, onUploadAnother }) {
  return (
    <div className="success-banner">
      <div className="success-banner__header">
        <CheckIcon className="success-banner__icon" />
        <h3 className="success-banner__title">Upload Successful</h3>
      </div>
      <p className="success-banner__message">
        Zone data uploaded successfully at {formatTimestamp(timestamp)}
      </p>
      <div className="success-banner__actions">
        <a href="/wireless_config" className="success-banner__link">View in Wireless Config</a>
        <Button variant="success" onClick={onUploadAnother}>
          Upload Another
        </Button>
      </div>
    </div>
  )
}
